let lastWindowState = window.innerWidth > 1025 ? "large" : "small";

class Modal {
    static $instances = [];
    static $openStack = []; // the open modals, the last opened last: Esc closes the top one

    // Esc does what the top open modal's X does (Prashant, COOL 367): not while its X is hidden (a step that must not
    // be left), and not when a widget inside used the key itself (select2 closing its list)
    static closeTopOnEscape(event) {
        if (event.key !== "Escape" || event.defaultPrevented) {
            return;
        }
        if (document.getElementById("yes-no-modal")?.getClientRects().length) { // a yes/no confirm (tm-yes-no-modal.js) sits on top: leave both
            return;
        }
        const top = Modal.$openStack.filter((modal) => modal.modal.classList.contains("open")).pop();
        if (top && top.closeButton && !top.closeButton.classList.contains("c-hidden") && top.closeButton.getClientRects().length) {
            top.closeButton.click();
        }
    }

    static instance(modalIdentifier) {
        return Modal.$instances[modalIdentifier] ?? null;
    }

    static add(modalIdentifier) {
        let modal = document.querySelector(modalIdentifier);
        if (modal) {
            Modal.$instances[modalIdentifier] = new Modal(modalIdentifier, modal);
        }
        return Modal.instance(modalIdentifier);
    }

    // A window as a dialog, opted in per window (Edit booking's page-local helper, booking-change.js dialog(), shared for the
    // gift card windows at PR #835's review; every other modal on the site is as it was): the window's box is a dialog named
    // by its title as it reads when the window opens (or by the aria-label components/modal.php gives it with 'dialog-label'),
    // the keyboard goes into the window when it opens — to firstControl()'s element when one is given and can take it, else
    // the box itself —, Tab and Shift+Tab turn round at the window's ends, and on closing the focus goes back to what opened
    // it, when that is still on the page.
    static dialog(selector, firstControl = null) {
        const $window = jQuery(selector);
        const $content = $window.find(".c-modal-content").first();
        const $title = $window.find("#c-modal-header-title").first();
        if (!$content.length || $content.data("tmDialog")) {
            return;
        }
        $content.data("tmDialog", true);
        const labelled = $content.attr("aria-label") !== undefined;
        let from = null; // what the window was opened from
        const canFocus = (node) => node.getClientRects().length > 0 && window.getComputedStyle(node).visibility !== "hidden" && !jQuery(node).is(":disabled");
        const tabbable = () => $content.find("a[href], button, input, select, textarea, iframe, [tabindex]").filter(function () {
            return this.tabIndex >= 0 && canFocus(this);
        }).get();
        $content.attr({ role: "dialog", "aria-modal": "true", tabindex: "-1" });
        jQuery(document).on("tmModal_open_" + selector, () => {
            if (!labelled && $title.length) {
                $content.attr("aria-label", $title.text().trim());
            }
            if (!$content[0].contains(document.activeElement)) {
                from = document.activeElement;
            }
            const focusIn = () => {
                let control = firstControl ? firstControl() : null;
                if (!control || !canFocus(control)) {
                    control = $content[0];
                }
                control.focus({ preventScroll: true });
                return document.activeElement === control;
            };
            if (!focusIn()) {
                setTimeout(focusIn, 60); // the window shows a moment after open()
            }
        });
        jQuery(document).on("tmModal_closed_" + selector, () => {
            const back = from;
            from = null;
            if (back && back.offsetParent !== null) { // (the body has none) otherwise the focus stays where the page put it
                back.focus({ preventScroll: true });
            }
        });
        jQuery(document).on("keydown", (e) => {
            const top = Modal.$openStack[Modal.$openStack.length - 1];
            if (e.key !== "Tab" || !top || top.modal !== $window[0]) { // the window on top traps, while open() has it on the stack (close() takes it off at once, before a sheet's slide-out ends)
                return;
            }
            const nodes = tabbable();
            if (!nodes.length) {
                return;
            }
            const at = nodes.indexOf(document.activeElement);
            if (at === -1 || at === (e.shiftKey ? 0 : nodes.length - 1)) { // outside the window, on its box or at an end: round to the other end
                e.preventDefault();
                nodes[e.shiftKey ? nodes.length - 1 : 0].focus({ preventScroll: true });
            }
        });
    }

    constructor(modalIdentifier, modal) {
        this.modalIdentifier = modalIdentifier;
        this.modal = modal;
        this.headerTitle = this.modal.querySelector("#c-modal-header-title");
        this.overlay = this.modal.querySelector(".c-modal-overlay");
        this.closeButton = this.modal.querySelector(".c-modal-close-button");
        this.backButton = this.modal.querySelector(".c-modal-back-button");
        this.closeModalEls = this.modal.querySelector(".c-modal-close-el");
        this.content = this.modal.querySelector(".c-modal-content");
        this.modalLoader = this.modal.querySelector('.c-loader-wrapper');
        this.modalBody = this.modal.querySelector('.c-modal-body');
        this.modalBodyInner = this.modal.querySelector('.c-modal-body-inner');
        this.attachEvents();
    }

    showOverlay(){
        this.modalBody.classList.add('c-modal-body-overlay-loader');
    }

    hideOverlay(){
        this.modalBody.classList.remove('c-modal-body-overlay-loader');
    }

    doNotAllowCloseOnBody() {
        this.overlay.classList.add('c-modal-prevent-close-on-body');
    }

    allowCloseOnBody() {
        this.overlay.classList.remove('c-modal-prevent-close-on-body');
    }

    showCloseIcon() {
        this.closeButton.classList.remove('c-hidden');
    }

    hideCloseIcon() {
        this.closeButton.classList.add('c-hidden');
    }

    setTitle(title) {
        this.headerTitle.innerText = title;
    }

    showLoader(withOverlay = true) {
        if (withOverlay) {
            this.modalBody.classList.add('c-modal-body-overlay-loader');
        }
        else {
            tmUtils.hideElementsForSelector(this.modalBodyInner);
        }
        tmUtils.showElementsForSelector(this.modalLoader);
    }

    hideLoader() {
        tmUtils.hideElementsForSelector(this.modalLoader);
        tmUtils.showElementsForSelector(this.modalBodyInner);
        this.modalBody.classList.remove('c-modal-body-overlay-loader');
    }

    attachEvents() {
        // Modals opened from the mobile menu panel (they carry a back button): the back arrow closes the modal and
        // leaves the visitor on the menu panel underneath (re-showing it if needed); the X leaves the whole thing —
        // modal AND menu panel — back on the page (Peter, 2026-09-13).
        const isMobileMenuModal = () => this.backButton && window.innerWidth <= 1024 && window.mobileLeftMenu && window.mobileLeftMenu.menuContainer;
        const closeBackToMenu = () => {
            this.close();
            if (isMobileMenuModal() && !window.mobileLeftMenu.menuContainer.classList.contains("show")) {
                setTimeout(() => window.mobileLeftMenu.showMenu(), 320);
            }
        };
        const closeAll = () => {
            this.close();
            if (isMobileMenuModal() && window.mobileLeftMenu.menuContainer.classList.contains("show")) {
                window.mobileLeftMenu.hideMenu();
            }
        };
        this.closeButton?.addEventListener("click", closeAll);
        this.backButton?.addEventListener("click", closeBackToMenu);

        this.closeModalEls?.addEventListener("click", () => this.close());

        this.overlay?.addEventListener("click", (event) => {
            if (!this.content.contains(event.target) && !this.overlay.classList.contains('c-modal-prevent-close-on-body')) {
                this.close();
            }
        });

        const backToTopElements = this.overlay.querySelectorAll('a');

        if ( backToTopElements.length ){
            backToTopElements.forEach(function(element) {
                if (element.textContent.includes('Back to top')) {
                    element.addEventListener('click', function(e) {
                        e.preventDefault();
                        const modalBody = element.closest('.c-modal-body');
                        if (modalBody) {
                            modalBody.scrollTo({
                                top: 0,
                                behavior: 'smooth'
                            });
                        }
                    });
                }
            });
        }
    }

    open( tab = null ) {
        Modal.$openStack = Modal.$openStack.filter((modal) => modal !== this).concat(this);
        this.modal.classList.add("open");
        setTimeout(() => this.modal.querySelector(".c-modal-content").classList.add("open"), 100);

        if ( jQuery(this.modal).find(".c-modal-desktop-r-chat-mobile-footer-slide-in").length ){
            if (lastWindowState === "large") {
                $("body").removeClass("body-no-scroll");
                $("html").removeClass("html-modal-open");
            } else {
                $("body").addClass("body-no-scroll");
                $("html").addClass("html-modal-open");
            }
        }else{
            document.body.classList.add("body-no-scroll");
            document.querySelector("html").classList.add("html-modal-open");
        }

        if (  tab !== null ){
            this.modal.querySelector('[data-tab-item-key="' + tab + '"]').click();
        }

        if ( tab === null && this.modal.querySelector('[data-tab-item-key]') ){
            this.modal.querySelector('[data-tab-item-key]').click();
        }

        jQuery(document).trigger('tmModal_open_' + this.modalIdentifier);
    }

    close() {
        Modal.$openStack = Modal.$openStack.filter((modal) => modal !== this);
        let modalTimeOutMs = 0;

        this.modal.querySelector(".c-modal-content").classList.remove("open");
        this.modal.querySelector(".c-modal-content").classList.add("closing");
        if ( this.modal.querySelector(".c-modal-desktop-r-chat-mobile-footer-slide-in") ) {
            modalTimeOutMs = 400;

            setTimeout( () => {
                this.modal.classList.remove("open")
                this.modal.querySelector(".c-modal-content").classList.remove("closing");
            }, modalTimeOutMs);
        }

        // the help centre's sheets are bottom sheets on desktop too (modal.scss #help-center-right-side): they slide out there as well
        const isSheet = window.innerWidth <= 1025 || this.modal.closest("#help-center-right-side");
        if ( this.modal.querySelector(".c-modal-desktop-r-mobile-f") || ( isSheet && this.modal.querySelector(".c-modal-mobile-footer-slide-in, .c-modal-mobile-left-slide-in") ) ) {
            modalTimeOutMs = 250;

            if ( this.modal.querySelector(".c-modal-mobile-footer-slide-in") ) {
                modalTimeOutMs = 550;
            }
            if ( this.modal.querySelector(".c-modal-mobile-left-slide-in") ) {
                modalTimeOutMs = 300; // leftSlideOut .3s
            }
            setTimeout( () => {
                this.modal.classList.remove("open")
                this.modal.querySelector(".c-modal-content").classList.remove("closing");
            }, modalTimeOutMs);
        }
        else {
            this.modal.classList.remove("open");
        }

        if (document.querySelectorAll(".overlay-show").length === 0) {
            document.body.classList.remove("body-no-scroll");
            document.querySelector("html").classList.remove("html-modal-open");
        }
        jQuery(document).trigger('tmModal_closed_' + this.modalIdentifier);
    }
}


jQuery(document).on('attachAllDocumentModals', function () {
    document.querySelectorAll("[data-modal-target]").forEach(modalSelector => {
        if (modalSelector.getAttribute("data-modal-events-attached") !== '1') {
            let modalIdentifier = modalSelector.getAttribute("data-modal-target");
            let tab = modalSelector.getAttribute("data-modal-tab") ?? null;
            let modal = Modal.add(modalIdentifier);
            if (modal) {
                if ( typeof tmUtils !== 'undefined' && tmUtils.isMobileOrTablet() && modalSelector.getAttribute('data-modal-target') === '#c-language-modal' ){
                    // Touch devices: open on a real tap only. A plain touchstart also fired while the visitor
                    // started scrolling the mobile menu panel over these rows.
                    let touchStart = null;
                    modalSelector.addEventListener('touchstart', (e) => {
                        const t = e.touches[0];
                        touchStart = { x: t.clientX, y: t.clientY, time: Date.now() };
                    }, { passive: true });
                    modalSelector.addEventListener('touchmove', (e) => {
                        if (!touchStart) { return; }
                        const t = e.touches[0];
                        if (Math.abs(t.clientX - touchStart.x) > 10 || Math.abs(t.clientY - touchStart.y) > 10) { touchStart = null; }
                    }, { passive: true });
                    modalSelector.addEventListener('touchend', (e) => {
                        if (touchStart && Date.now() - touchStart.time < 600) {
                            e.preventDefault(); // no synthetic click behind the modal
                            modal.open(tab);
                        }
                        touchStart = null;
                    });
                    modalSelector.addEventListener('touchcancel', () => { touchStart = null; });
                }
                else {
                    modalSelector.addEventListener('click', () => modal.open(tab));
                }
            }
            modalSelector.setAttribute("data-modal-events-attached", "1");
        }
    });
});

/* Initialise for all models */
document.addEventListener("keydown", Modal.closeTopOnEscape);
document.addEventListener("DOMContentLoaded", () => {
    $(window).on("resize", handleResize);
    jQuery(document).trigger('attachAllDocumentModals');
});

function handleResize() {
    let currentState = window.innerWidth > 1025 ? "large" : "small";
    if (currentState !== lastWindowState) {
        lastWindowState = currentState;
    }

    // Check modal open only if modal exists
    let $modal = $(".c-modal-desktop-r-chat-mobile-footer-slide-in .open");
    if ($modal.length) {
        if (currentState === "large") {
            $("body").removeClass("body-no-scroll");
            $("html").removeClass("html-modal-open");
        } else {
            $("body").addClass("body-no-scroll");
            $("html").addClass("html-modal-open");
        }
    }
}
